/**
 * The public career site and offer-acceptance contract — the ONE surface in the system reachable
 * with no session at all, same trust tier as auth.contract.ts's register/forgot-password routes.
 *
 * Every route here resolves its tenant from `:orgSlug` in the path (never a header/body — same
 * rule as auth's `orgSlug`, see that file's header) via the `resolve_org_by_slug` bootstrap SQL
 * function (packages/db/prisma/sql/rls.sql), then runs the real read/write inside
 * `withTenant(org.id, ...)` — it never queries with no tenant set. `offer/:offerToken` and
 * `slot-offer/:token` routes don't carry an org slug at all: the token itself, like an
 * email-verification or password-reset token, is the bearer credential that resolves both the org
 * and the row — `resolve_offer_by_token`/`resolve_slot_offer_by_token` in rls.sql.
 *
 * NO PII BEYOND WHAT A CANDIDATE THEMSELVES SUBMITTED IS EVER RETURNED HERE. `publicJobPostingSchema`
 * carries nothing about pay bands beyond what the requisition already publishes, and applying
 * returns only a bare confirmation — never the created candidate's internal id shape or pipeline
 * state, which would leak internal process to an anonymous caller.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  assessmentStatusSchema,
  candidateStageSchema,
  candidateStatusSchema,
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  emailSchema,
  employmentTypeSchema,
  errorBodySchema,
  experienceLevelSchema,
  offerSourceTypeSchema,
  offerStatusSchema,
  paginatedSchema,
  paginationQuerySchema,
  phoneSchema,
  uuidSchema,
  workTypeSchema,
} from '../schemas/common.js';
import {
  assessmentQuestionSchema,
  assessmentAnswerSchema,
  ctcBreakupSchema,
} from './recruitment.contract.js';
import { POSTING_QUESTION_KINDS } from '../enums.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const publicJobPostingSchema = z.object({
  id: uuidSchema,
  slug: z.string(),
  title: z.string(),
  description: z.string(),
  requirements: z.string().nullable(),
  departmentName: z.string(),
  locationName: z.string().nullable(),
  workType: workTypeSchema,
  employmentType: employmentTypeSchema,
  minSalary: decimalStringSchema.nullable(),
  maxSalary: decimalStringSchema.nullable(),
  currency: currencySchema,
  postedAt: z.string(),
  /** The apply-form questions, without their knockout rules (a candidate must not be able to read
   *  what would pass). */
  questions: z.array(
    z.object({
      id: uuidSchema,
      label: z.string(),
      helpText: z.string().nullable(),
      kind: z.enum(POSTING_QUESTION_KINDS),
      required: z.boolean(),
      options: z.array(z.string()),
    }),
  ),
});
export type PublicJobPosting = z.infer<typeof publicJobPostingSchema>;

/** The careers pages' own look and words, and the privacy notice an applicant agrees to. */
export const publicCareersPageSchema = z.object({
  orgName: z.string(),
  headline: z.string().nullable(),
  about: z.string().nullable(),
  logoUrl: z.string().nullable(),
  bannerUrl: z.string().nullable(),
  accentColor: z.string().nullable(),
  privacyNotice: z.string(),
  privacyVersion: z.number().int().positive(),
  retentionMonths: z.number().int().positive(),
  /** The company has WhatsApp switched on and verified, so the apply form can offer updates there. */
  whatsappAvailable: z.boolean(),
});
export type PublicCareersPage = z.infer<typeof publicCareersPageSchema>;

/** What a candidate may ask about their own application: its reference and the email they used. */
export const applicationStatusBodySchema = z.object({
  applicationRef: z.string().trim().min(5).max(40),
  email: emailSchema,
});

/** Plain words for a candidate, whatever the company named its internal stages. */
export const applicationStatusSchema = z.object({
  orgName: z.string(),
  postingTitle: z.string(),
  appliedAt: z.string(),
  stage: candidateStageSchema,
  status: candidateStatusSchema,
  headline: z.string(),
  detail: z.string(),
  upcomingInterviews: z.array(
    z.object({
      roundName: z.string(),
      scheduledAt: z.string(),
      durationMinutes: z.number().int().positive(),
      meetingUrl: z.string().url().nullable(),
    }),
  ),
  canWithdraw: z.boolean(),
  /** WhatsApp updates are on for this application, to the number ending in `whatsappLast4`. */
  whatsappUpdates: z.boolean(),
  whatsappLast4: z.string().nullable(),
  /** The company has a verified, switched-on WhatsApp connection, so turning updates on can work. */
  whatsappAvailable: z.boolean(),
});
export type ApplicationStatus = z.infer<typeof applicationStatusSchema>;

/** Switch WhatsApp updates on (with a number) or off, from the status page. */
export const setWhatsappUpdatesBodySchema = applicationStatusBodySchema.extend({
  enabled: z.boolean(),
  phone: phoneSchema.optional(),
});

export const withdrawApplicationBodySchema = applicationStatusBodySchema.extend({
  reason: z.string().trim().max(300).optional(),
});

/** What an agency sees through its own link. */
export const publicAgencyPortalSchema = z.object({
  agencyName: z.string(),
  orgName: z.string(),
  postings: z.array(
    z.object({
      id: uuidSchema,
      slug: z.string(),
      title: z.string(),
      departmentName: z.string(),
      locationName: z.string().nullable(),
      workType: workTypeSchema,
      employmentType: employmentTypeSchema,
    }),
  ),
});
export type PublicAgencyPortal = z.infer<typeof publicAgencyPortalSchema>;

export const agencySubmissionBodySchema = z.object({
  postingId: uuidSchema,
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: emailSchema,
  phone: phoneSchema.optional(),
  resumeUrl: z.string().url().optional(),
  note: z.string().trim().max(1000).optional(),
  /** The agency confirms the candidate agreed to be put forward. */
  consent: z.literal(true, { message: 'Confirm the candidate agreed to be submitted' }),
});

export const agencySubmissionSchema = z.object({
  candidateName: z.string(),
  postingTitle: z.string(),
  submittedAt: z.string(),
  /** In plain words: Received, In review, Interviewing, Selected, Not moving forward. */
  status: z.string(),
});

export const applicationReceivedSchema = z.object({
  received: z.literal(true),
  /** A short, opaque reference for the candidate's own use — NOT the internal candidate id, see
   *  this file's header comment. */
  applicationRef: z.string(),
});

/**
 * Only what a candidate needs to decide, and only while the offer is in a state where their
 * decision is meaningful — the route 404s a token whose offer is still `draft`/`pending_approval`.
 */
export const publicOfferSchema = z.object({
  orgName: z.string(),
  /** 1 for the first offer, higher for a revision. */
  version: z.number().int().positive(),
  candidateName: z.string(),
  postingTitle: z.string(),
  designation: z.string().nullable(),
  annualCtc: decimalStringSchema,
  currency: currencySchema,
  targetJoinDate: dateOnlySchema,
  status: offerStatusSchema,
  sourceType: offerSourceTypeSchema,
  /** The template's {{TOKEN}}-substituted full letter text, when the offer was created against
   * one — null for an offer with no template, in which case the plain fields above are all there
   * is to show. */
  renderedBody: z.string().nullable(),
  /** Always null now: the PDF is private, fetched through `downloadOfferDocument` while
   *  `hasDocument` is true. Kept so older clients don't break. */
  documentUrl: z.string().url().nullable(),
  /** True when the offer is a recruiter-uploaded PDF (`sourceType: 'upload'`). */
  hasDocument: z.boolean(),
  /** The last moment the candidate can respond. Once passed, `status` reads `expired`. */
  expiresAt: z.string().nullable(),
  /** The salary structure behind the CTC, when the offer was built on one. */
  ctcBreakup: ctcBreakupSchema.nullable(),
  /** The company signatory, with their signature image when the company uploaded one. */
  signatory: z
    .object({ name: z.string(), designation: z.string(), image: z.string().nullable() })
    .nullable(),
  /** The candidate's own e-signature, once accepted. */
  signedName: z.string().nullable(),
  signedAt: z.string().nullable(),
  /** The template's own branding images, when the offer was created against one with any set —
   * cosmetic only, shown as a header above the letter text. */
  letterheadUrls: z.array(z.string().url()),
});
export type PublicOffer = z.infer<typeof publicOfferSchema>;

export const publicOfferFileSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  contentBase64: z.string(),
});

/** A candidate viewing/booking a recruiter-proposed "pick a slot" link — see
 * recruitment.contract.ts's interviewSlotOfferSchema for why these times are proposed, not
 * computed from anyone's real calendar. */
export const publicSlotOfferSchema = z.object({
  orgName: z.string(),
  candidateName: z.string(),
  roundName: z.string(),
  durationMinutes: z.number().int().positive(),
  slots: z.array(z.string()),
  status: z.enum(['open', 'booked', 'expired', 'cancelled']),
  bookedSlotIndex: z.number().int().nullable(),
});
export type PublicSlotOffer = z.infer<typeof publicSlotOfferSchema>;

export const bookSlotOfferBodySchema = z.object({
  slotIndex: z.number().int().nonnegative(),
});

/** A candidate viewing/answering their own assessment via its emailed link — the questions are the
 * snapshot taken when it was sent (see recruitment.contract.ts's `candidateAssessmentSchema`), so
 * this never changes underneath them even if the org edits the template afterward. */
export const publicAssessmentSchema = z.object({
  orgName: z.string(),
  candidateName: z.string(),
  templateName: z.string(),
  questions: z.array(assessmentQuestionSchema),
  status: assessmentStatusSchema,
  answers: z.array(assessmentAnswerSchema).nullable(),
  expiresAt: z.string().nullable(),
});
export type PublicAssessment = z.infer<typeof publicAssessmentSchema>;

export const submitAssessmentBodySchema = z.object({
  answers: z.array(assessmentAnswerSchema).min(1),
});

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const listPublicPostingsQuerySchema = paginationQuerySchema.extend({
  department: z.string().trim().max(100).optional(),
  workType: workTypeSchema.optional(),
  q: z.string().trim().max(200).optional(),
});

export const applyToPostingBodySchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: emailSchema,
  phone: phoneSchema.optional(),
  resumeUrl: z.string().url().optional(),
  linkedinUrl: z.string().url().optional(),
  experienceLevel: experienceLevelSchema.optional(),
  skillTags: z.array(z.string().trim().min(1).max(100)).max(20).optional(),
  coverNote: z.string().trim().max(2000).optional(),
  /** The Cloudflare Turnstile response from the apply form. Required only where the server has
   *  Turnstile configured (TURNSTILE_SECRET_KEY); ignored otherwise. */
  captchaToken: z.string().max(4096).optional(),
  /** Tick-box: send me updates about this application on WhatsApp, to `phone`. Needs a phone. */
  whatsappOptIn: z.boolean().optional(),
  /** Agreement to the privacy notice, which the page shows at `privacyVersion`. Required. */
  consent: z.literal(true, { message: 'Please agree to the privacy notice to apply' }),
  consentVersion: z.number().int().positive().optional(),
  /** Where the candidate came from, from the link's `?source=` (anything else is "career site"). */
  source: z.enum(['linkedin', 'indeed', 'naukri', 'google_jobs', 'social', 'other']).optional(),
  answers: z
    .array(z.object({ questionId: uuidSchema, answer: z.string().trim().max(2000) }))
    .max(20)
    .optional(),
});

/** Accepting is signing: the candidate types their full name and agrees it is their signature. The
 *  drawn image is optional (a nicer letter); the typed name, consent and the evidence the server
 *  records (time, address, device, a hash of exactly what they saw) are what make it binding. */
export const respondToOfferBodySchema = z
  .object({
    decision: z.enum(['accepted', 'declined']),
    signatureName: z.string().trim().min(2).max(200).optional(),
    consent: z.boolean().optional(),
    signatureImage: z
      .string()
      .max(200_000)
      .regex(/^data:image\/png;base64,[A-Za-z0-9+/=]+$/, 'The drawn signature must be a PNG')
      .optional(),
  })
  .refine((b) => b.decision !== 'accepted' || (!!b.signatureName && b.consent === true), {
    message: 'Type your full name and tick the box to sign your acceptance',
    path: ['signatureName'],
  });

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const careersContract = c.router(
  {
    listPostings: {
      method: 'GET',
      path: '/:orgSlug/jobs',
      query: listPublicPostingsQuerySchema,
      responses: { 200: paginatedSchema(publicJobPostingSchema), 404: errorBodySchema },
      summary: "An org's published, public job postings — no session required",
      metadata: routeMeta('public'),
    },
    getApplicationStatus: {
      method: 'POST',
      path: '/:orgSlug/status',
      body: applicationStatusBodySchema,
      responses: { 200: applicationStatusSchema, 404: errorBodySchema },
      summary: 'A candidate checking on their own application with its reference and email',
      metadata: routeMeta('public'),
    },
    setWhatsappUpdates: {
      method: 'POST',
      path: '/:orgSlug/status/whatsapp',
      body: setWhatsappUpdatesBodySchema,
      responses: { 200: applicationStatusSchema, 404: errorBodySchema, 422: errorBodySchema },
      summary: 'A candidate turning WhatsApp updates on or off for their own application',
      metadata: routeMeta('public'),
    },
    withdrawApplication: {
      method: 'POST',
      path: '/:orgSlug/status/withdraw',
      body: withdrawApplicationBodySchema,
      responses: { 200: applicationStatusSchema, 404: errorBodySchema, 409: errorBodySchema },
      summary: 'A candidate withdrawing their own application',
      metadata: routeMeta('public'),
    },
    getAgencyPortal: {
      method: 'GET',
      path: '/:orgSlug/agency/:agencyToken',
      responses: { 200: publicAgencyPortalSchema, 404: errorBodySchema },
      summary: "An agency's own page: the roles it can submit candidates to",
      metadata: routeMeta('public'),
    },
    submitAgencyCandidate: {
      method: 'POST',
      path: '/:orgSlug/agency/:agencyToken/submit',
      body: agencySubmissionBodySchema,
      responses: {
        201: applicationReceivedSchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'An agency putting a candidate forward for a role',
      metadata: routeMeta('public'),
    },
    listAgencySubmissions: {
      method: 'GET',
      path: '/:orgSlug/agency/:agencyToken/submissions',
      responses: { 200: z.array(agencySubmissionSchema), 404: errorBodySchema },
      summary: "An agency's own submissions and where each stands",
      metadata: routeMeta('public'),
    },
    getCareersPage: {
      method: 'GET',
      path: '/:orgSlug/page',
      responses: { 200: publicCareersPageSchema, 404: errorBodySchema },
      summary: "An org's careers page branding and privacy notice — no session required",
      metadata: routeMeta('public'),
    },
    getPosting: {
      method: 'GET',
      path: '/:orgSlug/jobs/:postingSlug',
      responses: { 200: publicJobPostingSchema, 404: errorBodySchema },
      summary: 'One published job posting — no session required',
      metadata: routeMeta('public'),
    },
    apply: {
      method: 'POST',
      path: '/:orgSlug/jobs/:postingSlug/apply',
      body: applyToPostingBodySchema,
      responses: {
        201: applicationReceivedSchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Apply to a published posting — no session required',
      metadata: routeMeta('public'),
    },
    getOfferByToken: {
      method: 'GET',
      path: '/offer/:offerToken',
      responses: { 200: publicOfferSchema, 404: errorBodySchema },
      summary: 'A candidate reviewing their own offer via its emailed link',
      metadata: routeMeta('public'),
    },
    downloadSignedOffer: {
      method: 'GET',
      path: '/offer/:offerToken/signed',
      responses: { 200: publicOfferFileSchema, 404: errorBodySchema },
      summary: 'A candidate downloading their signed offer, once they have accepted',
      metadata: routeMeta('public', { ai: 'hidden' }),
    },
    downloadOfferDocument: {
      method: 'GET',
      path: '/offer/:offerToken/document',
      responses: { 200: publicOfferFileSchema, 404: errorBodySchema },
      summary: "A candidate downloading their own offer PDF via the emailed link",
      metadata: routeMeta('public', { ai: 'hidden' }),
    },
    respondToOffer: {
      method: 'POST',
      path: '/offer/:offerToken/respond',
      body: respondToOfferBodySchema,
      responses: {
        200: publicOfferSchema,
        // PLAN_LIMIT_REACHED: the free-trial employee cap (see employees.create).
        402: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Accept or decline an offer via its emailed link',
      metadata: routeMeta('public'),
    },
    getSlotOfferByToken: {
      method: 'GET',
      path: '/slot-offer/:token',
      responses: { 200: publicSlotOfferSchema, 404: errorBodySchema },
      summary: 'A candidate viewing their proposed interview time slots via its emailed link',
      metadata: routeMeta('public'),
    },
    bookSlotOffer: {
      method: 'POST',
      path: '/slot-offer/:token/book',
      body: bookSlotOfferBodySchema,
      responses: {
        200: publicSlotOfferSchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Book one of the proposed slots — creates the interview',
      metadata: routeMeta('public'),
    },
    getAssessmentByToken: {
      method: 'GET',
      path: '/assessment/:token',
      responses: { 200: publicAssessmentSchema, 404: errorBodySchema },
      summary: 'A candidate viewing their own assessment via its emailed link',
      metadata: routeMeta('public'),
    },
    submitAssessment: {
      method: 'POST',
      path: '/assessment/:token/submit',
      body: submitAssessmentBodySchema,
      responses: {
        200: publicAssessmentSchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Submit answers to an assessment via its emailed link',
      metadata: routeMeta('public'),
    },
  },
  { pathPrefix: '/careers' },
);
